import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectSelectedCompany } from "../redux-toolkit/features/auth/authSlice";

/** Normalize company id from string, ObjectId-like, or populated `{ _id }` document. */
export function resolveCompanyId(companyRef) {
  if (!companyRef) return null;
  if (typeof companyRef === "string") return companyRef;
  if (typeof companyRef === "object" && companyRef._id != null) {
    return resolveCompanyId(companyRef._id);
  }
  if (typeof companyRef.toString === "function") {
    const asString = companyRef.toString();
    if (asString && asString !== "[object Object]") return asString;
  }
  return null;
}

/** Selected company from switcher, else primary `companyId` / `company` on auth user. */
export function resolveActiveCompanyId(selectedCompany, user) {
  return (
    resolveCompanyId(selectedCompany) ||
    resolveCompanyId(user?.companyId) ||
    resolveCompanyId(user?.company)
  );
}

export function getCompanyContextRedirect(pathname, newCompanyId) {
  if (!pathname) return null;

  if (
    pathname.includes("organizational-quest/edit-books") ||
    pathname.includes("organizational-quest/view-books")
  ) {
    return "/super-admin/manage-organization/organizational-quest";
  }

  if (/\/my-workspace\/(edit|view)-(books|courses)\//.test(pathname)) {
    return "/admin/my-workspace";
  }

  if (
    pathname.includes("/manage-company/task-verification/") &&
    !pathname.endsWith("/task-verification")
  ) {
    return "/admin/manage-company/task-verification";
  }

  if (
    pathname.includes("/manage-organization/task-verification/") &&
    !pathname.endsWith("/task-verification")
  ) {
    return "/super-admin/manage-organization/task-verification";
  }

  if (
    /\/manage-company\/employee\/[^/]+\/[^/]+\/activity/.test(pathname) &&
    newCompanyId
  ) {
    return `/admin/manage-company/employee/${newCompanyId}`;
  }

  if (pathname.includes("/manage-company/employee/") && newCompanyId) {
    return `/admin/manage-company/employee/${newCompanyId}`;
  }

  if (pathname.includes("/manage-organization/companies/books/")) {
    return "/super-admin/manage-organization/companies";
  }

  return null;
}

/**
 * Runs closeFn when selectedCompany changes after initial mount (user switched company).
 */
export function useCloseOnCompanySwitch(closeFn) {
  const selectedCompany = useSelector(selectSelectedCompany);
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    closeFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- closeFn may be inline; selectedCompany is the trigger
  }, [selectedCompany]);
}
